import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, CardContent, CardHeader, CardTitle,
} from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Checkbox } from '../ui/checkbox';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../ui/dialog';
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid,
} from 'recharts';
import { GitCompare, TrendingUp, Copy, ArrowRight, ArrowUp, ArrowDown, Minus } from 'lucide-react';
import { toast } from 'sonner';
import { costingsAPI, historyAPI } from '../../services/api';

const fmtMoney = (n, ccy = 'INR') => {
  if (n === null || n === undefined) return '-';
  const sym = ccy === 'INR' ? '₹' : ccy === 'USD' ? '$' : ccy === 'EUR' ? '€' : (ccy || '') + ' ';
  return `${sym}${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 4 })}`;
};

const fmtDate = (s) => {
  if (!s) return '-';
  try { return new Date(s).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }); }
  catch { return s; }
};

const statusColor = (s) => {
  const x = String(s || '').toLowerCase();
  if (['approved', 'locked'].includes(x)) return 'bg-emerald-100 text-emerald-700';
  if (['rejected'].includes(x)) return 'bg-red-100 text-red-700';
  if (['draft'].includes(x)) return 'bg-slate-100 text-slate-700';
  return 'bg-blue-100 text-blue-700';
};

function DeltaCell({ row }) {
  const pct = row.delta_pct;
  if (pct === null || pct === undefined) return <span className="text-muted-foreground">—</span>;
  if (Math.abs(pct) < 0.01) return <span className="inline-flex items-center text-slate-500"><Minus className="h-3 w-3 mr-1" />0%</span>;
  // Margin delta is good when up; cost deltas are bad when up. Color neutrally.
  const isCost = ['rm_price_per_kg', 'rm_cost_per_piece', 'total_cost_per_piece', 'total_cost_per_kg', 'tooling_costs', 'setup_costs'].includes(row.field);
  const isMargin = row.field === 'margin_percentage';
  const up = pct > 0;
  const good = isMargin ? up : (isCost ? !up : null);
  const cls = good === null ? 'text-slate-700' : good ? 'text-emerald-600 font-semibold' : 'text-red-600 font-semibold';
  const Icon = up ? ArrowUp : ArrowDown;
  return <span className={`inline-flex items-center ${cls}`}><Icon className="h-3 w-3 mr-1" />{up ? '+' : ''}{pct.toFixed(2)}%</span>;
}

export function CostingComparisonDialog({ open, onOpenChange, baselineId, compareId, currency }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = React.useCallback(() => {
    if (!baselineId || !compareId) return;
    setLoading(true);
    setError(null);
    historyAPI.comparCostings(baselineId, compareId)
      .then((res) => setData(res.data))
      .catch((err) => {
        const detail = err.response?.data?.detail || 'Could not load the comparison. Please try again.';
        setError(detail);
        toast.error(detail);
      })
      .finally(() => setLoading(false));
  }, [baselineId, compareId]);

  React.useEffect(() => {
    if (!open) return;
    load();
  }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitCompare className="h-5 w-5" />Costing Comparison</DialogTitle>
          <DialogDescription>Field-by-field delta between two costing versions.</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="py-12 flex justify-center" data-testid="costing-compare-loading"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" /></div>
        ) : error || !data ? (
          <div className="py-10 text-center space-y-3" data-testid="costing-compare-error">
            <p className="text-sm text-red-600">{error || 'No comparison data available.'}</p>
            <Button variant="outline" size="sm" onClick={load} data-testid="costing-compare-retry">Try again</Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 text-sm">
              <Card><CardContent className="py-3 px-4">
                <p className="text-[11px] uppercase font-semibold text-muted-foreground">Baseline</p>
                <p className="font-mono font-bold">{data.baseline.costing_number} <span className="text-muted-foreground">v{data.baseline.version_number}</span></p>
                <p className="text-xs text-muted-foreground">{fmtDate(data.baseline.created_at)} · {data.baseline.status}</p>
              </CardContent></Card>
              <Card><CardContent className="py-3 px-4">
                <p className="text-[11px] uppercase font-semibold text-muted-foreground">Compare</p>
                <p className="font-mono font-bold">{data.compare.costing_number} <span className="text-muted-foreground">v{data.compare.version_number}</span></p>
                <p className="text-xs text-muted-foreground">{fmtDate(data.compare.created_at)} · {data.compare.status}</p>
              </CardContent></Card>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Metric</TableHead>
                  <TableHead className="text-right">Baseline</TableHead>
                  <TableHead className="w-10 text-center">→</TableHead>
                  <TableHead className="text-right">Compare</TableHead>
                  <TableHead className="text-right">Δ Abs</TableHead>
                  <TableHead className="text-right">Δ %</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.deltas.map((row) => (
                  <TableRow key={row.field} data-testid={`delta-row-${row.field}`}>
                    <TableCell className="font-medium">{row.label}</TableCell>
                    <TableCell className="text-right font-mono">{Number(row.baseline).toLocaleString('en-IN', { maximumFractionDigits: 4 })}</TableCell>
                    <TableCell className="text-center text-muted-foreground"><ArrowRight className="h-3 w-3 inline" /></TableCell>
                    <TableCell className="text-right font-mono">{Number(row.compare).toLocaleString('en-IN', { maximumFractionDigits: 4 })}</TableCell>
                    <TableCell className="text-right font-mono">{row.delta_abs > 0 ? '+' : ''}{Number(row.delta_abs).toLocaleString('en-IN', { maximumFractionDigits: 4 })}</TableCell>
                    <TableCell className="text-right"><DeltaCell row={row} /></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            {data.process_flow_diff && (
              <Card><CardContent className="py-3 px-4 text-sm">
                <p className="font-semibold mb-1">Process Route</p>
                <p className="text-muted-foreground">
                  Baseline v{data.process_flow_diff.baseline_version || '?'} had <strong>{data.process_flow_diff.baseline_steps.length}</strong> steps;
                  Compare v{data.process_flow_diff.compare_version || '?'} has <strong>{data.process_flow_diff.compare_steps.length}</strong> steps
                  ({data.process_flow_diff.step_count_delta > 0 ? '+' : ''}{data.process_flow_diff.step_count_delta}).
                </p>
              </CardContent></Card>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PartHistoryTab({ partId, history, currency = 'INR', onReload }) {
  const navigate = useNavigate();
  const [selected, setSelected] = useState([]);
  const [cmpOpen, setCmpOpen] = useState(false);

  const costings = useMemo(() => (history?.costings || []).slice().reverse(), [history]);

  const toggle = (id) => {
    setSelected((sel) => {
      if (sel.includes(id)) return sel.filter((x) => x !== id);
      if (sel.length >= 2) return [sel[1], id];
      return [...sel, id];
    });
  };

  const chartData = useMemo(() => costings.map((c) => ({
    label: `v${c.version_number || '?'}`,
    created: fmtDate(c.created_at),
    rm: c.raw_material?.raw_material_cost_per_piece || 0,
    total: c.total_cost_per_piece || 0,
    margin: c.margin_percentage || 0,
    selling: c.selling_price_per_piece || 0,
  })), [costings]);

  const handleClone = async (sourceId) => {
    try {
      const res = await costingsAPI.clone(sourceId);
      toast.success(`Cloned as new draft (v${res.data.version})`);
      if (onReload) onReload();
      navigate(`/costings/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Clone failed');
    }
  };

  if (!history) return null;

  return (
    <div className="space-y-6">
      {/* Trend chart */}
      {chartData.length >= 2 && (
        <Card data-testid="costing-trend-chart">
          <CardHeader className="pb-2">
            <CardTitle className="text-base uppercase tracking-wide flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Cost & Margin Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-72 w-full" style={{ minHeight: '288px' }}>
              <ResponsiveContainer width="99%" height="100%">
                <LineChart data={chartData} margin={{ top: 8, right: 12, bottom: 8, left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis dataKey="label" fontSize={11} />
                  <YAxis yAxisId="left" fontSize={11} />
                  <YAxis yAxisId="right" orientation="right" fontSize={11} unit="%" />
                  <Tooltip
                    contentStyle={{ fontSize: '12px' }}
                    formatter={(val, name) => (name === 'Margin %' ? `${Number(val).toFixed(2)}%` : fmtMoney(val, currency))}
                  />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line yAxisId="left" type="monotone" dataKey="rm" name="RM Cost/pc" stroke="#3b82f6" strokeWidth={2} />
                  <Line yAxisId="left" type="monotone" dataKey="total" name="Total Cost/pc" stroke="#f59e0b" strokeWidth={2} />
                  <Line yAxisId="left" type="monotone" dataKey="selling" name="Selling Price/pc" stroke="#10b981" strokeWidth={2} />
                  <Line yAxisId="right" type="monotone" dataKey="margin" name="Margin %" stroke="#a855f7" strokeWidth={2} strokeDasharray="5 5" />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Costing history with compare */}
      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base uppercase tracking-wide">All Costings ({history.costings?.length || 0})</CardTitle>
          <div className="flex items-center gap-2">
            <p className="text-xs text-muted-foreground hidden md:block">
              {selected.length === 0 ? 'Select 2 costings to compare' : selected.length === 1 ? 'Select 1 more' : 'Ready'}
            </p>
            <Button
              size="sm" variant="outline"
              disabled={selected.length !== 2}
              onClick={() => setCmpOpen(true)}
              data-testid="open-compare-btn"
            >
              <GitCompare className="h-4 w-4 mr-1.5" />Compare
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-8"></TableHead>
                <TableHead>Costing #</TableHead>
                <TableHead>Version</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">RM/pc</TableHead>
                <TableHead className="text-right">Total/pc</TableHead>
                <TableHead className="text-right">Margin %</TableHead>
                <TableHead className="text-right">SP/pc</TableHead>
                <TableHead>Created</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(history.costings || []).map((c) => (
                <TableRow key={c.id} className={selected.includes(c.id) ? 'bg-blue-50/60' : 'hover:bg-muted/40'} data-testid={`costing-row-${c.id}`}>
                  <TableCell>
                    <Checkbox
                      checked={selected.includes(c.id)}
                      onCheckedChange={() => toggle(c.id)}
                      data-testid={`compare-checkbox-${c.id}`}
                    />
                  </TableCell>
                  <TableCell className="font-mono font-medium cursor-pointer text-blue-700 hover:underline" onClick={() => navigate(`/costings/${c.id}`)}>
                    {c.costing_number}
                  </TableCell>
                  <TableCell>v{c.version_number}{c.is_active && <Badge variant="outline" className="ml-1 text-[10px]">active</Badge>}</TableCell>
                  <TableCell><Badge className={statusColor(c.status)} variant="outline">{c.status}</Badge></TableCell>
                  <TableCell className="text-right font-mono">{fmtMoney(c.raw_material?.raw_material_cost_per_piece, currency)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtMoney(c.total_cost_per_piece, currency)}</TableCell>
                  <TableCell className="text-right font-mono">{(c.margin_percentage || 0).toFixed(2)}%</TableCell>
                  <TableCell className="text-right font-mono font-semibold">{fmtMoney(c.selling_price_per_piece, currency)}</TableCell>
                  <TableCell className="text-xs">{fmtDate(c.created_at)}</TableCell>
                  <TableCell>
                    <Button size="sm" variant="ghost" onClick={() => handleClone(c.id)} data-testid={`clone-costing-${c.id}`}>
                      <Copy className="h-3.5 w-3.5 mr-1" />Clone
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {(!history.costings || history.costings.length === 0) && (
                <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-6">No costings yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Quotations */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base uppercase tracking-wide">Quotations using this part ({history.quotations?.length || 0})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Quotation #</TableHead><TableHead>Customer</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Unit Price</TableHead><TableHead className="text-right">Line Total</TableHead><TableHead>Date</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {(history.quotations || []).map((q) => (
                <TableRow key={q.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/quotations/${q.id}`)}>
                  <TableCell className="font-mono font-medium">{q.quotation_number}</TableCell>
                  <TableCell>{q.customer_name}</TableCell>
                  <TableCell><Badge className={statusColor(q.status)} variant="outline">{q.status}</Badge></TableCell>
                  <TableCell className="text-right font-mono">{Math.round(q.line_quantity || 0).toLocaleString('en-IN')}</TableCell>
                  <TableCell className="text-right font-mono">{fmtMoney(q.line_unit_price, q.currency)}</TableCell>
                  <TableCell className="text-right font-mono">{fmtMoney(q.line_total, q.currency)}</TableCell>
                  <TableCell className="text-xs">{fmtDate(q.created_at)}</TableCell>
                </TableRow>
              ))}
              {(!history.quotations || history.quotations.length === 0) && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No quotations yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Sales Orders */}
      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base uppercase tracking-wide">Sales Orders ({history.orders?.length || 0})</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow><TableHead>Order #</TableHead><TableHead>Customer</TableHead><TableHead>PO</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Qty</TableHead><TableHead className="text-right">Unit Price</TableHead><TableHead>Date</TableHead></TableRow>
            </TableHeader>
            <TableBody>
              {(history.orders || []).map((o) => (
                <TableRow key={o.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/orders/${o.id}`)}>
                  <TableCell className="font-mono font-medium">{o.order_number}</TableCell>
                  <TableCell>{o.customer_name}</TableCell>
                  <TableCell className="font-mono text-xs">{o.customer_po_number || '-'}</TableCell>
                  <TableCell><Badge className={statusColor(o.status)} variant="outline">{o.status}</Badge></TableCell>
                  <TableCell className="text-right font-mono">{Math.round(o.line_quantity || 0).toLocaleString('en-IN')}</TableCell>
                  <TableCell className="text-right font-mono">{fmtMoney(o.line_unit_price, o.currency)}</TableCell>
                  <TableCell className="text-xs">{fmtDate(o.created_at)}</TableCell>
                </TableRow>
              ))}
              {(!history.orders || history.orders.length === 0) && (
                <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground py-6">No orders yet</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Process flow revisions */}
      {history.process_flows && history.process_flows.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base uppercase tracking-wide">Process Flow Versions ({history.process_flows.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Version</TableHead><TableHead>Status</TableHead><TableHead className="text-right">Steps</TableHead><TableHead>Created</TableHead><TableHead></TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {history.process_flows.map((f) => (
                  <TableRow key={f.id} className="hover:bg-muted/40">
                    <TableCell>v{f.version_number}{f.is_active && <Badge variant="outline" className="ml-1 text-[10px]">active</Badge>}</TableCell>
                    <TableCell><Badge className={statusColor(f.status)} variant="outline">{f.status}</Badge></TableCell>
                    <TableCell className="text-right font-mono">{(f.steps || []).length}</TableCell>
                    <TableCell className="text-xs">{fmtDate(f.created_at)}</TableCell>
                    <TableCell><Button size="sm" variant="ghost" onClick={() => navigate(`/process-flows/${f.id}`)}>View</Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Inquiries (RFQs) */}
      {history.inquiries && history.inquiries.length > 0 && (
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base uppercase tracking-wide">RFQs ({history.inquiries.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow><TableHead>Number</TableHead><TableHead>Customer</TableHead><TableHead>Status</TableHead><TableHead>Date</TableHead></TableRow>
              </TableHeader>
              <TableBody>
                {history.inquiries.map((inq) => (
                  <TableRow key={inq.id} className="cursor-pointer hover:bg-muted/40" onClick={() => navigate(`/inquiries/${inq.id}`)}>
                    <TableCell className="font-mono font-medium">{inq.inquiry_number}</TableCell>
                    <TableCell>{inq.customer_name}</TableCell>
                    <TableCell><Badge className={statusColor(inq.status)} variant="outline">{inq.status}</Badge></TableCell>
                    <TableCell className="text-xs">{fmtDate(inq.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <CostingComparisonDialog
        open={cmpOpen}
        onOpenChange={setCmpOpen}
        baselineId={selected[0]}
        compareId={selected[1]}
        currency={currency}
      />
    </div>
  );
}
